// --- TYPE DEFINITIONS ---
interface VitalSignsResults {
    bloodPressure?: { value: { systolic: number; diastolic: number } };
    pulseRate?: { value: number, confidenceLevel: number };
    respirationRate?: { value: number, confidenceLevel: number };
    wellnessIndex?: { value: number };
    wellnessLevel?: { value: number };
    stressLevel?: { value: number };
    stressIndex?: { value: number };
    pnsIndex?: { value: number };
    snsIndex?: { value: number };
    pnsZone?: { value: number };
    snsZone?: { value: number };
    lfhf?: { value: number };
    meanRri?: { value: number, confidenceLevel: number };
    sdnn?: { value: number, confidenceLevel: number };
    rmssd?: { value: number };
    sd1?: { value: number };
    sd2?: { value: number };
    prq?: { value: number, confidenceLevel: number };
}

interface Summary {
    value: string;
    category: string;
    interpretation: string;
    confidenceLevel?: number;
}

// --- THRESHOLD DEFINITIONS (Verified with SDK Document) ---
const BP_SYSTOLIC = { LOW: 100, NORMAL: 129 };
const HEART_RATE = { LOW: 60, HIGH: 100 };
const RESPIRATION_RATE = { LOW: 12, HIGH: 20 };
const HRV_SDNN = { NORMAL_MIN: 50 };
const PRQ_RANGES = { NORMAL_LOW: 4.0, NORMAL_HIGH: 6.0 };
const LFHF_RATIO = { NORMAL_LOW: 0.27, NORMAL_HIGH: 0.38 };
const WELLNESS_SCORE_RANGES = { LOW: 3, MEDIUM: 7 };
const STRESS_INDEX_RANGES = { LOW: 50, NORMAL: 150, MODERATE: 500 };

// --- Individual Metric Processor Functions ---

const getBloodPressureSummary = (bpData: VitalSignsResults['bloodPressure']): Summary | null => {
    if (!bpData?.value) return null;
    const { systolic, diastolic } = bpData.value;

    let category: string;
    let interpretation: string;

    if (systolic >= 90 && systolic <= 130 && diastolic >= 60 && diastolic <= 80) {
        category = 'Normal';
        interpretation = 'Your blood pressure is within the normal range (90/60 – 130/80 mmHg).';
    } else if ((systolic >= 131 && systolic <= 139) || (diastolic >= 81 && diastolic <= 89)) {
        category = 'Fair / Monitor';
        interpretation = 'Your blood pressure is slightly elevated (131–139 / 81–89 mmHg). Monitoring is recommended.';
    } else if (systolic >= 140 || diastolic >= 90 || systolic < 90) {
        category = 'Needs Attention';
        interpretation = 'Your blood pressure is significantly outside the normal range (≥140/90 or <90 systolic). Medical attention may be required.';
    } else {
        category = 'Unknown';
        interpretation = 'Unable to categorize blood pressure values.';
    }

    return {
        value: `${systolic}/${diastolic} mmHg`,
        category,
        interpretation,
    };
};



const getHeartRateSummary = (hrData: VitalSignsResults['pulseRate']): Summary | null => {
    if (!hrData?.value) return null;
    const { value } = hrData;

    let category: string;
    let interpretation: string;

    if (value >= 60 && value <= 90) {
        category = 'Normal';
        interpretation = 'Your heart rate is within the normal resting range of 60–90 bpm.';
    } else if ((value >= 50 && value <= 59) || (value >= 91 && value <= 99)) {
        category = 'Fair / Monitor';
        interpretation = 'Your heart rate is slightly outside the normal range. Monitor your condition.';
    } else {
        category = 'Needs Attention';
        interpretation = 'Your heart rate is significantly outside the normal range. Medical attention may be required.';
    }

    return {
        value: `${value} bpm`,
        category,
        interpretation,
        confidenceLevel: hrData.confidenceLevel,
    };
};



const getRespirationSummary = (respirationData: VitalSignsResults['respirationRate']): Summary | null => {
    if (!respirationData?.value) return null;
    const { value } = respirationData;

    let category: string;
    let interpretation: string;

    if (value >= 12 && value <= 20) {
        category = 'Normal';
        interpretation = 'Your respiration rate is within the normal range of 12–20 breaths per minute.';
    } else if ((value >= 10 && value <= 11) || (value >= 21 && value <= 24)) {
        category = 'Fair / Monitor';
        interpretation = 'Your respiration rate is slightly outside the normal range. Monitor your breathing closely.';
    } else {
        category = 'Needs Attention';
        interpretation = 'Your respiration rate is significantly outside the normal range. Medical attention may be required.';
    }

    return {
        value: `${value} breaths/min`,
        category,
        interpretation,
        confidenceLevel: respirationData.confidenceLevel,
    };
};


const getWellnessSummary = (
    wellnessData: VitalSignsResults['wellnessIndex']
): Summary | null => {
    if (!wellnessData?.value) return null;
    const { value } = wellnessData;
    let category: string;
    let interpretation: string;

    if (value >= 8 && value <= 10) {
        category = "Normal";
        interpretation = "Your wellness score indicates strong overall health resilience and lower cardiovascular risk.";
    } else if (value >= 5 && value <= 7) {
        category = "Fair / Monitor";
        interpretation = "Your wellness score is moderate. Consider healthy lifestyle adjustments to improve resilience.";
    } else {
        category = "Needs Attention";
        interpretation = "Your wellness score suggests elevated cardiovascular risk. Monitoring and preventive action are recommended.";
    }

    return {
        value: `Level ${value}/10`,
        category,
        interpretation,
    };
};

const getWellnessLevelSummary = (wellnessData: VitalSignsResults['wellnessIndex']): Summary | null => {
    if (!wellnessData?.value) return null;
    const { value } = wellnessData;
    let category: string;
    if (value <= WELLNESS_SCORE_RANGES.LOW) category = 'Low';
    else if (value <= WELLNESS_SCORE_RANGES.MEDIUM) category = 'Medium';
    else category = 'High';
    return { value: `${value} out of 10`, category, interpretation: 'A score used to predict cardiovascular risk over the next 5-10 years. A higher score indicates lower cardiovascular risk.' };
};

const getStressLevelSummary = (stressData: VitalSignsResults['stressLevel']): Summary | null => {
    if (!stressData?.value) return null;
    const { value } = stressData;

    let category: string;
    let interpretation: string;

    if (value >= 1 && value <= 2) {
        category = 'Normal';
        interpretation = 'Your stress score is within the calm range (1–2), indicating good relaxation and balance.';
    } else if (value == 3) {
        category = 'Fair / Monitor';
        interpretation = 'Your stress score is moderately elevated (3). Monitoring and stress management may be beneficial.';
    } else {
        category = 'Needs Attention';
        interpretation = 'Your stress score is high (4–5), suggesting significant stress. Active stress reduction or medical advice may be required.';
    }

    return {
        value: `Level ${value}/5`,
        category,
        interpretation,
    };
};


const getStressIndexSummary = (stressIndexData: VitalSignsResults['stressIndex']): Summary | null => {
    if (!stressIndexData?.value) return null;
    const { value } = stressIndexData;

    let category: string;
    let interpretation: string;

    if (value >= 0 && value <= 150) {
        category = 'Normal';
        interpretation = 'Your stress index is within the normal range (0–150), indicating balanced autonomic function.';
    } else if (value >= 151 && value <= 500) {
        category = 'Fair / Monitor';
        interpretation = 'Your stress index is moderately elevated (151–500). Monitoring and stress management techniques may help.';
    } else {
        category = 'Needs Attention';
        interpretation = 'Your stress index is high (>500), which may indicate excessive stress or autonomic imbalance and requires medical attention.';
    }

    return {
        value: `${value}`,
        category,
        interpretation,
    };
};


const getAutonomicBalanceSummary = (
    pnsIndex: VitalSignsResults['pnsIndex'],
    snsIndex: VitalSignsResults['snsIndex'],
    pnsZone: VitalSignsResults['pnsZone'],
    snsZone: VitalSignsResults['snsZone']
): Summary | null => {
    if (
        pnsIndex?.value === undefined ||
        snsIndex?.value === undefined ||
        pnsZone?.value === undefined ||
        snsZone?.value === undefined
    )
        return null;

    const ratio = snsIndex.value / (pnsIndex.value || 1); // avoid divide by zero
    let category: string;
    let interpretation: string;

    if (ratio >= 0.5 && ratio <= 2.0) {
        category = "Normal";
        interpretation = "Your autonomic balance (Sympathetic:Parasympathetic) is within the healthy range (0.5–2.0).";
    } else if ((ratio >= 0.3 && ratio < 0.5) || (ratio > 2.0 && ratio <= 3.0)) {
        category = "Fair / Monitor";
        interpretation = "Your autonomic balance is moderately outside the optimal range. Monitoring and lifestyle adjustments may help.";
    } else {
        category = "Needs Attention";
        interpretation = "Your autonomic balance shows significant imbalance (<0.3 or >3.0), suggesting strong dominance of one system. Stress management or medical advice may be beneficial.";
    }

    return {
        value: `Ratio: ${ratio.toFixed(2)} | PNS Zone: ${pnsZone.value} | SNS Zone: ${snsZone.value}`,
        category,
        interpretation,
    };
};


const getLfhfSummary = (lfhfData: VitalSignsResults['lfhf']): Summary | null => {
    if (!lfhfData?.value) return null;
    const { value } = lfhfData;
    let category: string;
    if (value < LFHF_RATIO.NORMAL_LOW) category = 'Parasympathetic Dominant';
    else if (value <= LFHF_RATIO.NORMAL_HIGH) category = 'Normal';
    else category = 'Sympathetic Dominant';
    return { value: value.toFixed(2), category, interpretation: 'Reflects the balance between sympathetic (LF) and parasympathetic (HF) activity. A higher ratio indicates an increased Sympathetic activity.' };
};

const getMeanRriSummary = (rriData: VitalSignsResults['meanRri']): Summary | null => {
    if (!rriData?.value) return null;
    const { value } = rriData;

    let category: string;
    let interpretation: string;

    if (value >= 600 && value <= 1000) {
        category = 'Normal';
        interpretation = 'Your Mean RRi is in the normal range (600–1000 ms), indicating a healthy average interval between heartbeats.';
    } else if ((value >= 500 && value <= 599) || (value >= 1001 && value <= 1100)) {
        category = 'Fair / Monitor';
        interpretation = 'Your Mean RRi is slightly outside the optimal range (500–599 ms or 1001–1100 ms). Monitoring is recommended.';
    } else {
        category = 'Needs Attention';
        interpretation = 'Your Mean RRi is very low (<500 ms) or very high (>1100 ms), which may indicate abnormal heart rhythm or autonomic imbalance and requires medical attention.';
    }

    return {
        value: `${value} ms`,
        category,
        interpretation,
        confidenceLevel: rriData.confidenceLevel,
    };
};


const getHrvSdnnSummary = (sdnnData: VitalSignsResults['sdnn']): Summary | null => {
    if (!sdnnData?.value) return null;
    const { value } = sdnnData;

    let category: string;
    let interpretation: string;

    if (value >= 30) {
        category = 'Normal';
        interpretation = 'Your HRV SDNN is in the healthy range (≥30 ms), indicating good autonomic balance.';
    } else if (value >= 20 && value <= 29) {
        category = 'Fair / Monitor';
        interpretation = 'Your HRV SDNN is slightly reduced (20–29 ms). Monitoring is recommended as it may indicate lower resilience to stress.';
    } else {
        category = 'Needs Attention';
        interpretation = 'Your HRV SDNN is very low (<20 ms). This may indicate poor autonomic function or high stress and requires medical attention.';
    }

    return {
        value: `${value} ms`,
        category,
        interpretation,
        confidenceLevel: sdnnData.confidenceLevel,
    };
};


const getHrvRmssdSummary = (rmssdData: VitalSignsResults['rmssd']): Summary | null => {
    if (!rmssdData?.value) return null;
    const { value } = rmssdData;

    let category: string;
    let interpretation: string;

    if (value >= 25) {
        category = 'Normal';
        interpretation = 'Your HRV RMSSD is in the healthy range (≥25 ms), reflecting good parasympathetic ("rest and digest") activity.';
    } else if (value >= 15 && value <= 24) {
        category = 'Fair / Monitor';
        interpretation = 'Your HRV RMSSD is moderately reduced (15–24 ms). Monitoring is recommended as it may indicate reduced vagal tone.';
    } else {
        category = 'Needs Attention';
        interpretation = 'Your HRV RMSSD is very low (<10 ms). This may indicate poor parasympathetic function or chronic stress and requires medical attention.';
    }

    return {
        value: `${value} ms`,
        category,
        interpretation,
    };
};


const getPoincareSummary = (sd1Data: VitalSignsResults['sd1'], sd2Data: VitalSignsResults['sd2']): Summary | null => {
    if (!sd1Data?.value || !sd2Data?.value) return null;
    const sd1 = sd1Data.value;
    const sd2 = sd2Data.value;
    return { value: `SD1: ${sd1}, SD2: ${sd2}`, category: 'Normal', interpretation: 'SD1 is the standard deviation perpendicular to the line of identity, while SD2 is along the line of identity in a poincaré plot.' };
};

const getPrqSummary = (prqData: VitalSignsResults['prq']): Summary | null => {
    if (!prqData?.value) return null;
    const { value } = prqData;

    let category: string;
    let interpretation: string;

    if (value >= 3.0 && value <= 5.0) {
        category = 'Normal';
        interpretation = 'Your PRQ is within the healthy range (3.0–5.0). This indicates a balanced relationship between pulse and breathing.';
    } else if ((value >= 2.5 && value <= 2.9) || (value >= 5.1 && value <= 6.5)) {
        category = 'Fair / Monitor';
        interpretation = 'Your PRQ is slightly outside the normal range. Monitoring is recommended as it may indicate imbalance between pulse and respiration.';
    } else if (value < 2.5 || value > 6.5) {
        category = 'Needs Attention';
        interpretation = 'Your PRQ is significantly outside the normal range. This may signal underlying health issues and requires medical attention.';
    } else {
        category = 'Unknown';
        interpretation = 'Unable to categorize PRQ value.';
    }

    return {
        value: value.toFixed(1),
        category,
        interpretation,
        confidenceLevel: prqData.confidenceLevel,
    };
};


export const processVitalSigns = (results: VitalSignsResults) => {
    const summaries = {
        bloodPressure: getBloodPressureSummary(results.bloodPressure),
        heartRate: getHeartRateSummary(results.pulseRate),
        respirationRate: getRespirationSummary(results.respirationRate),
        wellnessIndex: getWellnessSummary(results.wellnessIndex),
        wellnessLevel: getWellnessLevelSummary(results.wellnessLevel),
        stressLevel: getStressLevelSummary(results.stressLevel),
        stressIndex: getStressIndexSummary(results.stressIndex),
        autonomicBalance: getAutonomicBalanceSummary(results.pnsIndex, results.snsIndex, results.pnsZone, results.snsZone),
        lfhfRatio: getLfhfSummary(results.lfhf),
        meanRri: getMeanRriSummary(results.meanRri),
        hrvSdnn: getHrvSdnnSummary(results.sdnn),
        hrvRmssd: getHrvRmssdSummary(results.rmssd),
        poincare: getPoincareSummary(results.sd1, results.sd2),
        prq: getPrqSummary(results.prq),
    };

    // FIX: Replaced Object.fromEntries with a compatible .reduce() method.
    return Object.entries(summaries)
        .filter(([, value]) => value !== null)
        .reduce((acc, [key, value]) => {
            acc[key] = value as Summary;
            return acc;
        }, {} as { [key: string]: Summary });
};